/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useRef, useState } from 'react';
import { useFormik } from 'formik';
import {
    Button,
    Checkbox,
    Combobox,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    DialogTrigger,
    Field,
    Input,
    Link,
    Option,
    OptionOnSelectData,
    Text,
    Textarea,
    makeStyles,
    tokens,
} from '@fluentui/react-components';
import { DatePicker } from '@fluentui/react-datepicker-compat';
import { AttachRegular, CalendarAddRegular, CalendarEditRegular } from '@fluentui/react-icons';
import { useNavigate } from 'react-router';
import { Column, Row } from '../common';
import { CaseExpense, useInitialValues } from '../../data/types';
import { useCaseDataService } from '../../data/services/useCaseDataService';
import { useCommonStyles } from '../common/Styles/commonStyles';
import { useFileIcons } from '../common/useFileIcons';
import { CaseExpenseService } from '../../data/services/useCaseExpenseService';
import { useAccountState } from '../../redux/reducers/accountReducer';
import { ExpenseWithAttachment, useValidation } from '../../data/types/validation';
import { InputCurrency } from '../controls/InputCurrency';
import { useCaseService } from '../../data/services';
import { useDialogControls } from './useDialogControls';
import { useAccess } from '../../auth/useAccess';
import { Paths } from '../common/routes';
import { SettingsPageTabs } from '../pages/Settings/AccountSettingsPage';

const ADD_EXPENSE_TYPES_OPTION = '__add-expense-types__';

const useStyles = makeStyles({
    uploadInput: {
        paddingInlineStart: '0px',
    },
    hiddenInput: {
        display: 'none',
    },
    amountInput: {
        fontWeight: tokens.fontWeightBold,
        fontSize: '1.5rem',
        maxWidth: '10rem',
        height: '4rem',
    },
});
export type AddEditCaseExpenseDialogProps = {
    caseId: string;
    onSubmit?: (values: any) => Promise<void>;
    expenseToEdit?: CaseExpense;
    children?: JSX.Element;
    open?: boolean;
    onOpenChange?: (isOpen: boolean) => void;
    expenseService?: CaseExpenseService;
};
export const AddEditCaseExpenseDialog = (props: AddEditCaseExpenseDialogProps) => {
    const { children, expenseToEdit, caseId, open, onOpenChange, expenseService } = props;

    //Hooks
    const initValues = useInitialValues();
    const caseDataService = useCaseDataService();
    const caseService = useCaseService();
    const caseObj = caseService.findCaseById(caseId);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const fileIcons = useFileIcons();
    const { primaryAccount } = useAccountState();
    const { validateCaseExpense } = useValidation();
    const access = useAccess(primaryAccount?.user);
    const navTo = useNavigate();

    //State
    const [selectedExpenseTypes, setSelectedExpenseTypes] = useState<string[]>([]);

    //Styles
    const commonStyles = useCommonStyles();
    const styles = useStyles();

    //Form
    const initFormValues = (): ExpenseWithAttachment => {
        return {
            attachment: null as File | null,
            expense: {
                ...initValues.caseExpense(caseId, primaryAccount?.user.username as string),
                ...(expenseToEdit && {
                    ...expenseToEdit,
                    date: expenseToEdit.date ? new Date(expenseToEdit.date) : undefined,
                }),
            },
        };
    };
    const expenseForm = useFormik<ExpenseWithAttachment>({
        initialValues: initFormValues(),
        onSubmit: async (values, { resetForm }) => {
            const expValues = values as ExpenseWithAttachment;

            if (!!expValues.expense.text) {
                if (!!expenseService) {
                    await expenseService.upsertCaseExpenseAsync(
                        expValues.expense as CaseExpense,
                        expValues.attachment ?? undefined,
                    );
                }

                resetForm({ values: initFormValues() });
            }
        },
        validationSchema: validateCaseExpense.schema,
    });

    //Dialog Controls
    const onCloseDialog = () => {
        expenseForm.resetForm({
            values: initFormValues(),
        });
    };
    const [dialogOpen, onOpenChangeHandler, closeDialog] = useDialogControls(open, onOpenChange, onCloseDialog);

    //UI Events
    const clickAddExpense = async (e: any) => {
        e.stopPropagation();
        await expenseForm.submitForm();
    };
    const clickCancel = (e: any) => {
        e.stopPropagation();
        expenseForm.resetForm({
            values: initFormValues(),
        });
        closeDialog();
    };
    const onSelectDate = (date: Date | null | undefined) => {
        expenseForm.setFieldValue('expense.date', date || undefined);
    };
    const onOptionSelect = (_ev: any, data: OptionOnSelectData) => {
        if (data.optionValue === ADD_EXPENSE_TYPES_OPTION) {
            closeDialog();
            navTo(Paths.accountSettings, { state: { tab: SettingsPageTabs.caseData } });
            return;
        }

        setSelectedExpenseTypes(data.selectedOptions);
        expenseForm.setFieldValue('expense.type', data.optionText ?? '');
    };
    const onInput = (ev: React.ChangeEvent<HTMLInputElement>) => {
        expenseForm.setFieldValue('expense.type', ev.target.value);
    };
    const handleUploadBtnClick = (e: any) => {
        e.stopPropagation();

        fileInputRef.current?.click();
    };
    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            //console.log('Selected file:', file.name);
            expenseForm.setFieldValue('attachment', file);
            expenseForm.setFieldValue('expense.attachmentName', (file as File).name);
        }
    };

    //Effects
    useEffect(() => {
        if (dialogOpen) {
            expenseForm.resetForm({ values: initFormValues() });
        }
    }, [dialogOpen, caseId, expenseToEdit]);

    return (
        <Dialog open={dialogOpen} onOpenChange={onOpenChangeHandler}>
            <DialogTrigger disableButtonEnhancement>{children}</DialogTrigger>
            <DialogSurface aria-describedby={undefined} mountNode={undefined}>
                <DialogBody>
                    <DialogTitle>
                        <Row gap='medium'>
                            <Column width='auto' vAlign='middle'>
                                {!!expenseToEdit ? <CalendarEditRegular /> : <CalendarAddRegular />}
                            </Column>
                            <Column>{`${!!expenseToEdit ? 'Edit' : 'Add'} Case Expense`}</Column>
                        </Row>
                    </DialogTitle>
                    <DialogContent>
                        <Column>
                            {/* <Text size={200}>{JSON.stringify(expenseForm.errors)}</Text> */}
                            <Row>
                                <Column align='left'>
                                    <Field
                                        className={commonStyles.text.field}
                                        label='Expense Date'
                                        required
                                        validationMessage={expenseForm.errors.expense?.date}
                                    >
                                        <DatePicker
                                            name='date'
                                            value={expenseForm.values.expense?.date}
                                            onSelectDate={onSelectDate}
                                            placeholder='Select a date...'
                                            minDate={caseObj?.retainedDate ? new Date(caseObj.retainedDate) : undefined}
                                            data-testid='expense-dialog-date-picker'
                                        />
                                    </Field>
                                </Column>
                                <Column>
                                    <Field
                                        className={commonStyles.text.field}
                                        label='Expense Type'
                                        required
                                        validationMessage={expenseForm.errors.expense?.type}
                                    >
                                        <Combobox
                                            placeholder='Expense Type'
                                            value={expenseForm.values.expense?.type || ''}
                                            selectedOptions={[expenseForm.values.expense?.type || '']}
                                            onInput={onInput}
                                            onOptionSelect={onOptionSelect}
                                            data-testid='expense-dialog-expense-type-combobox'
                                        >
                                            {caseDataService.expenseTypes?.map((expType) => (
                                                <Option
                                                    key={expType}
                                                    data-testid={`expense-dialog-expense-type-option-${expType}`}
                                                >
                                                    {expType}
                                                </Option>
                                            ))}
                                            {!caseDataService.expenseTypes?.length &&
                                                (access.settings.canEditCaseDataTypes() ? (
                                                    <Option
                                                        value={ADD_EXPENSE_TYPES_OPTION}
                                                        text='Add expense types in Case Data'
                                                        data-testid='expense-dialog-add-expense-types-link'
                                                    >
                                                        <Column noGap align='center'>
                                                            <Text size={200}>No expense types available. </Text>
                                                            <Link>
                                                            <Text size={200}>
                                                            Go to Case Data to add expense types.
                                                            </Text>
                                                            </Link>
                                                        </Column>
                                                    </Option>
                                                ) : (
                                                    <Option
                                                        disabled
                                                        text='Contact an admin to add expense types'
                                                        data-testid='expense-dialog-no-expense-types-message'
                                                    >
                                                        Contact an admin to add expense types.
                                                    </Option>
                                                ))}
                                        </Combobox>
                                    </Field>
                                </Column>
                            </Row>
                            <Row>
                                <Column flex='1'>
                                    <Field
                                        className={commonStyles.text.field}
                                        label='Description'
                                        required
                                        hint={`${expenseForm.values.expense?.text?.length || 0} / ${validateCaseExpense.text.maxLength}`}
                                        validationMessage={expenseForm.errors.expense?.text}
                                    >
                                        <Textarea
                                            name='expense.text'
                                            value={expenseForm.values.expense?.text}
                                            onChange={expenseForm.handleChange}
                                            placeholder='Enter expense description'
                                            data-testid='expense-dialog-description-input'
                                            maxLength={validateCaseExpense.text.maxLength}
                                        />
                                    </Field>
                                </Column>
                                <Column width='fit-content' align='center' vAlign='middle'>
                                    <Checkbox
                                        label='Recoverable'
                                        checked={expenseForm.values.expense?.recoverable || false}
                                        onChange={(ev) => {
                                            expenseForm.setFieldValue('expense.recoverable', ev.currentTarget.checked);
                                        }}
                                        data-testid='expense-dialog-recoverable-checkbox'
                                    />
                                </Column>
                            </Row>
                            <Row>
                                <Column>
                                    <Field className={commonStyles.text.field} label='Attachment'>
                                        <input
                                            type='file'
                                            ref={fileInputRef}
                                            className={styles.hiddenInput}
                                            onChange={handleFileChange}
                                        />
                                        <Input
                                            className={styles.uploadInput}
                                            contentBefore={
                                                <Row gap='small'>
                                                    <Column>
                                                        <Button
                                                            className={commonStyles.button.root}
                                                            icon={<AttachRegular />}
                                                            appearance='secondary'
                                                            onClick={handleUploadBtnClick}
                                                            data-testid='expense-dialog-attachment-upload-button'
                                                        />
                                                    </Column>
                                                    <Column vAlign='middle'>
                                                        {expenseForm.values.expense?.attachmentName &&
                                                            fileIcons.getFileIcon(
                                                                expenseForm.values.expense?.attachmentName as string,
                                                            )}
                                                    </Column>
                                                </Row>
                                            }
                                            value={expenseForm.values.expense?.attachmentName}
                                            readOnly
                                            data-testid='expense-dialog-attachment-input'
                                        />
                                    </Field>
                                </Column>
                                <Column width='auto'>
                                    <Field
                                        className={commonStyles.text.field}
                                        label='Amount'
                                        required
                                        validationMessage={expenseForm.errors.expense?.amount}
                                    >
                                        <InputCurrency
                                            name='expense.amount'
                                            className={styles.amountInput}
                                            value={expenseForm.values.expense?.amount}
                                            onChange={expenseForm.handleChange}
                                            data-testid='expense-dialog-amount-input'
                                        />
                                    </Field>
                                </Column>
                            </Row>
                        </Column>
                    </DialogContent>
                    <DialogActions>
                        <DialogTrigger disableButtonEnhancement>
                            <Button
                                type='submit'
                                appearance='primary'
                                onClick={clickAddExpense}
                                disabled={!expenseForm.dirty || !expenseForm.isValid}
                                data-testid='expense-dialog-add-button'
                            >
                                Add Expense
                            </Button>
                        </DialogTrigger>
                        <DialogTrigger disableButtonEnhancement>
                            <Button appearance='secondary' onClick={clickCancel} data-testid='expense-dialog-cancel-button'>
                                Cancel
                            </Button>
                        </DialogTrigger>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
};
